import { useState } from "react";
import { router } from "@inertiajs/react";
import {
  AlertDialog,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  IconButton,
  Text,
  TextField,
} from "@radix-ui/themes";
import { Check, Music, Pencil, Trash2, X } from "lucide-react";
import { ManagerGallerySong } from "@/types/manager_gallery";
import ReorderControls from "./ReorderControls";
import UploadDropzone from "./UploadDropzone";
import { reorderedIds } from "./ordering";

interface Props {
  songs: ManagerGallerySong[];
  directUploadPath: string;
  songsPath: string;
  reorderPath: string;
  remainingBytes: number;
}

// The index signature is what lets a Draft go straight into `router.patch` as
// the request body — Inertia's FormDataConvertible needs one, and both fields
// here are strings anyway.
interface Draft {
  [key: string]: string;
  title: string;
  artist: string;
}

/** "03 - Some Track.mp3" → "03 - Some Track". A starting point to edit, not a
 *  guess at the real title — no attempt is made to split artist out of it. */
function titleFromFilename(name: string): string {
  return name.replace(/\.[^.]+$/, "");
}

function SongRow({
  song,
  index,
  total,
  onMove,
}: {
  song: ManagerGallerySong;
  index: number;
  total: number;
  onMove: (to: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>({
    title: song.title ?? "",
    artist: song.artist ?? "",
  });

  const save = () => {
    router.patch(song.path, { song: draft }, {
      preserveScroll: true,
      onSuccess: () => setEditing(false),
    });
  };

  const cancel = () => {
    setDraft({ title: song.title ?? "", artist: song.artist ?? "" });
    setEditing(false);
  };

  return (
    <Card>
      <Flex direction="column" gap="2">
        <Flex align="center" gap="3" wrap="wrap">
          <Badge variant="soft" color="gray" size="1">
            {index + 1}
          </Badge>

          {editing ? (
            <Flex direction={{ initial: "column", sm: "row" }} gap="2" style={{ flex: 1 }}>
              <TextField.Root
                placeholder="Title"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                style={{ flex: 1 }}
              />
              <TextField.Root
                placeholder="Artist"
                value={draft.artist}
                onChange={(e) => setDraft({ ...draft, artist: e.target.value })}
                style={{ flex: 1 }}
              />
            </Flex>
          ) : (
            <Box style={{ flex: 1, minWidth: 0 }}>
              {/* "Untitled track" is a display fallback only — Song stores these
                  as null, and the model deliberately allows it. */}
              <Text as="p" size="2" weight="medium" truncate>
                {song.title || "Untitled track"}
              </Text>
              <Text as="p" size="1" color="gray" truncate>
                {song.artist || "No artist"}
                {song.filename ? ` · ${song.filename}` : ""}
              </Text>
            </Box>
          )}

          <Flex align="center" gap="2">
            {editing ? (
              <>
                <IconButton size="1" variant="soft" color="green" onClick={save} aria-label="Save">
                  <Check size={13} />
                </IconButton>
                <IconButton size="1" variant="soft" color="gray" onClick={cancel} aria-label="Cancel">
                  <X size={13} />
                </IconButton>
              </>
            ) : (
              <>
                <ReorderControls index={index} total={total} onMove={onMove} />
                <IconButton
                  size="1"
                  variant="soft"
                  color="gray"
                  onClick={() => setEditing(true)}
                  aria-label="Edit track"
                >
                  <Pencil size={13} />
                </IconButton>
                <AlertDialog.Root>
                  <AlertDialog.Trigger>
                    <IconButton size="1" variant="soft" color="red" aria-label="Delete track">
                      <Trash2 size={13} />
                    </IconButton>
                  </AlertDialog.Trigger>
                  <AlertDialog.Content maxWidth="400px">
                    <AlertDialog.Title>Remove this track?</AlertDialog.Title>
                    <AlertDialog.Description size="2">
                      {song.title || "This track"} will be removed from the playlist,
                      and its audio deleted from storage. This cannot be undone.
                    </AlertDialog.Description>
                    <Flex gap="3" mt="4" justify="end">
                      <AlertDialog.Cancel>
                        <Button variant="soft" color="gray">
                          Keep
                        </Button>
                      </AlertDialog.Cancel>
                      <AlertDialog.Action>
                        <Button
                          variant="solid"
                          color="red"
                          onClick={() => router.delete(song.path, { preserveScroll: true })}
                        >
                          Remove
                        </Button>
                      </AlertDialog.Action>
                    </Flex>
                  </AlertDialog.Content>
                </AlertDialog.Root>
              </>
            )}
          </Flex>
        </Flex>

        {/* Native controls, `preload="none"` so a hundred-track playlist doesn't
            fetch a hundred files just to render. The point is confirming the
            right audio landed, not building a player. */}
        {song.file_url && (
          <audio controls preload="none" src={song.file_url} style={{ width: "100%", height: 32 }} />
        )}
      </Flex>
    </Card>
  );
}

export default function PlaylistEditor({
  songs,
  directUploadPath,
  songsPath,
  reorderPath,
  remainingBytes,
}: Props) {
  const moveTo = (from: number, to: number) => {
    router.patch(
      reorderPath,
      { ordered_ids: reorderedIds(songs, from, to) },
      { preserveScroll: true },
    );
  };

  return (
    <Flex direction="column" gap="3">
      <UploadDropzone
        directUploadPath={directUploadPath}
        accept="audio/*"
        title="Drag audio files here"
        hint="MP3s upload straight to storage. Titles start from the filename and can be edited after."
        remainingBytes={remainingBytes}
        onUploaded={(uploaded) =>
          router.post(
            songsPath,
            {
              songs: uploaded.map((u) => ({
                signed_id: u.signedId,
                title: titleFromFilename(u.file.name),
              })),
            },
            { preserveScroll: true },
          )
        }
      />

      {songs.length === 0 ? (
        <Card>
          <Flex align="center" gap="2">
            <Music size={14} color="var(--gray-9)" />
            <Text size="2" color="gray">
              No tracks yet.
            </Text>
          </Flex>
        </Card>
      ) : (
        songs.map((song, index) => (
          <SongRow
            key={song.id}
            song={song}
            index={index}
            total={songs.length}
            onMove={(to) => moveTo(index, to)}
          />
        ))
      )}
    </Flex>
  );
}
